import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnInit, OnDestroy, ViewChild, Injector } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { WFStepLocalResource, WfTaskLocalResource, WFStepLocalStatus } from 'src/app/components/workflow/workflow';
import { Configuration } from '../../../checklist-builder/checklist-builder-sections/checklist-builder-section/checklist-builder-section.component';
import { Checklist, ChecklistTemplate } from '../../../checklists';
import { ChecklistsService } from '../../../checklists.service';
import { ChecklistTasksComponent } from '../../checklist-tasks/checklist-tasks.component';


@Component({
  selector: "checklist-step",
  templateUrl: "./checklist-step.component.html",
  styleUrls: ["./checklist-step.component.scss"],
})
export class ChecklistStepComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentChecklist?: Checklist | null;
  @Input() currentChecklistTemplate?: ChecklistTemplate;
  @Input() step!: WFStepLocalResource;
  @Input() precheck!: boolean;
  @Input() disabled?: boolean;

  @Output() checkShared = new EventEmitter<any>();
  @Output() taskChanged = new EventEmitter<WfTaskLocalResource>();
  @Output() viewportChanged = new EventEmitter<void>();

  @ViewChild(ChecklistTasksComponent) tasksComponent!: ChecklistTasksComponent;
  hidden!: boolean;

  disableTasks?: boolean;

  tasks!: WfTaskLocalResource[];
  tasks$!: Observable<WfTaskLocalResource[]>;
  tasksSubs!: Subscription;

  checklists!: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;

  roles!: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  wfStepLocalStatus = WFStepLocalStatus;

  config!: Configuration;
  canDuplicate!: boolean;
  touched!: boolean;

  tooltip!: string;

  constructor(
    protected override injector: Injector,
    private service: ChecklistsService,
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    this.tasksSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.config = this.utils.JSONparse(this.step.configuration);
    if (!this.config) { this.config = new Configuration(); }
    this.loadRoles();
    this.canDuplicate = this.hasRoles(this.config.roleIDs)
    this.getTasks();
  }

  loadRoles() {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      if (data.length) {
        this.roles = data.filter(r => this.config.roleIDs?.includes(r.id));
        this.tooltip = 'Duplicate Step (Allowed roles: ' + this.roles?.map(r => r.code).join(', ') + ')';
      }
    });
  }

  /**
   * Refreshes task availability when the checklist or section-level disabled state changes.
   * @param changes Updated component inputs.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentChecklist']) {
      this.hidden = this.step.wfStepStatusLocals?.find(x => x.status == this.currentChecklist?.checklistStatusID)?.hidden ?? false;
      this.getTasks();
    }
    if (changes['currentChecklist'] || changes['disabled'])
      this.disableTasks = !this.step.wfStepStatusLocals.map(x => x.status).includes(this.currentChecklist?.checklistStatusID) || this.disabled;
  }

  /**
   * Indicates whether the step contains a task rendered by its condition and status behavior.
   */
  get hasVisibleTasks(): boolean {
    return this.tasks?.some(task => {
      const disableInsteadOfHide = !!this.utils.JSONparse(task.configuration)?.hideDisable;
      return task.required !== false &&
        (!this.disableTasks || !task.condition || disableInsteadOfHide);
    }) ?? false;
  }

  duplicate(e: any) {
    e.stopPropagation();
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: "400px",
      data: {
        message: this.getMessage("ChecklistDuplicateStepConfirmation").description,
        icon: "question",
      },
    });
    yesno.afterClosed().subscribe(async (data) => {
      if (data) {
        await this.service.duplicateStep(this.step.id).toPromise();
      }
    });
  }

  delete(e: any) {
    e.stopPropagation();
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: "400px",
      data: {
        message: this.getMessage("ChecklistDeleteStepConfirmation").description,
        icon: "question",
      },
    });
    yesno.afterClosed().subscribe(async (data) => {
      if (data) {
        await this.service.deleteStep(this.step.id).toPromise();
      }
    });
  }

  setDisabled() {
  }

  getTasks() {
    this.tasks = this.step?.wfTaskLocals;
    this.tasks.map(t => this.service.taskTouched(t));
    this.touched = this.tasks.some(t => t.isTouched);
  }

  isStepExpanded() {
    const expanded = this.step.collapsible ? !this.step.collapsed : true;
    return expanded;
  }

  /**
   * Saves step collapsed state and notifies the parent that visible task positions changed.
   * @param collapsed New collapsed state from the expansion panel.
   */
  saveState(collapsed: boolean) {
    this.viewportChanged.emit();
    if (collapsed != this.step.collapsed && this.step.collapsed != undefined && this.currentChecklist) {
      this.step.collapsed = collapsed;
      this.service.saveStepExpanded(this.currentChecklist.id, this.step.id, collapsed);
    }
  }

}
